import * as React from "react";
import {
  DataTable,
  TextField,
  ReferenceField,
  ChipField,
  BooleanField,
  ImageField,
  ReferenceArrayField,
  SingleFieldList,
  Show,
  ArrayField,
  NumberField,
  FunctionField,
  SelectField,
  usePermissions,
  useRecordContext,
  Title,
  TopToolbar,
  EditButton,
  Button,
  DateField,
  PrevNextButtons,
  useNotify,
  useDataProvider,
  useRefresh,
  LinearProgress,
} from "react-admin";
import { ROLE_TYPES } from "../enums/Roles";
import { AuditButton } from "../common/AuditButton";
import { getTimeFromDate, shortWeekdayToLong } from "../utils/DateTimeUtils";
import { DELIVERY_METHODS } from "../enums/DeliveryMethods";
import { TabbedShowLayout } from "../common/TabbedShowLayout";
import VideoField from "../common/VideoField";
import { PriceField } from "../common/PriceField";
import {
  PRODUCT_TYPES_OPTIONS,
  PURCHASE_SPLIT_OPTIONS,
} from "../utils/ProductUtils";
import { Archive } from "@mui/icons-material";
import { ENTITY_AVAILABILITY_STATUS } from "../enums/EntityAvailabilityStatus";
import { ENTITY_AVAILABILITY_STATUS_OPTIONS } from "../utils/EntityAvailabilityStatusUtils";
import { PRODUCT_TYPES } from "../enums/ProductTypes";
import ProductStockShowTab from "./ProductStockShowTab";

const ProductShowActions = () => {
  const { permissions } = usePermissions();
  const record = useRecordContext();
  const notify = useNotify();
  const [isLoading, setIsLoading] = React.useState(false);
  const dataProvider = useDataProvider();
  const refresh = useRefresh();

  const handleArchiveProduct = async () => {
    notify("Archivando producto...");
    setIsLoading(true);
    dataProvider
      .update("products", {
        data: {
          ...record,
          status: ENTITY_AVAILABILITY_STATUS.ARCHIVED,
        },
      })
      .then(() => {
        notify("Producto archivado con éxito", {
          type: "success",
        });
        refresh();
      })
      .catch(() => {
        notify("Ha ocurrido un error archivando el producto", {
          type: "error",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  if (isLoading) return <LinearProgress />;

  return (
    <TopToolbar>
      <EditButton />
      {permissions === ROLE_TYPES.ADMIN && (
        <AuditButton />
      )}
      {permissions === ROLE_TYPES.ADMIN &&
        record?.status !== ENTITY_AVAILABILITY_STATUS.ARCHIVED && (
          <Button onClick={handleArchiveProduct} label="Archivar">
            <Archive />
          </Button>
        )}
      <PrevNextButtons linkType="show" />
    </TopToolbar>
  );
};

const ProductTitle = () => {
  const record = useRecordContext();
  return <Title title={`Producto "${record?.name}"`} />;
};

const ProductShow = () => {
  const { permissions } = usePermissions();
  const record = useRecordContext();

  return (
    <>
      <TabbedShowLayout>
        <TabbedShowLayout.Tab label="Datos básicos">
          {permissions === ROLE_TYPES.ADMIN && (
            <TextField source="id" label="ID" />
          )}
          <TextField source="name" label="Nombre" />
          <TextField source="description" label="Descripción" />
          <PriceField
            source="price"
            country="restaurantData.country"
            label="Precio"
          />
          <ReferenceArrayField
            label="Categorías"
            reference="categories"
            source="categories"
          >
            <SingleFieldList linkType={false}>
              <ChipField source="name" />
            </SingleFieldList>
          </ReferenceArrayField>
          {permissions === ROLE_TYPES.ADMIN && (
            <ReferenceField
              label="Restaurante"
              reference="restaurants"
              source="restaurant"
            >
              <TextField source="name" label="Nombre" />
            </ReferenceField>
          )}
          <SelectField
            label="Tipo"
            source="type"
            choices={PRODUCT_TYPES_OPTIONS}
          />
          <FunctionField
            source="deliveryMethods"
            render={(record) =>
              `${record?.deliveryMethods
                ?.map((d) =>
                  d === DELIVERY_METHODS.TAKE_AWAY
                    ? "Retirar"
                    : d === DELIVERY_METHODS.ON_SITE
                      ? "Salón"
                      : ""
                )
                .join(", ")}`
            }
            label="Métodos de Entrega"
          />
          <SelectField
            label="Comisión"
            source="purchaseSplitOption"
            choices={PURCHASE_SPLIT_OPTIONS}
          />
          <NumberField
            source="customMorfySplitPercentage"
            label="Comisión personalizada"
            options={{
              style: "percent",
              maximumFractionDigits: 2,
            }}
            emptyText="-"
          />

          <SelectField
            label="Estado"
            source="status"
            choices={ENTITY_AVAILABILITY_STATUS_OPTIONS}
          />
          {permissions === ROLE_TYPES.ADMIN && (
            <BooleanField
              source="deactivationLocked"
              label={"Desactivación bloqueada"}
            />
          )}
          {record?.deactivatedAt && (
            <DateField
              label="Fecha de desactivación"
              source="deactivatedAt"
              showTime
            />
          )}
        </TabbedShowLayout.Tab>

        <TabbedShowLayout.Tab label="Descuentos">
          <ArrayField source="discounts" label="">
            <DataTable
              storeKey="product.discounts"
              rowClick={false}
              bulkActionButtons={false}
            >
              <DataTable.Col source="days" label="Días">
                <FunctionField
                  source="days"
                  render={(record) =>
                    `${record.days.map((d) => shortWeekdayToLong(d)).join(", ")}`
                  }
                  label="Días"
                />
              </DataTable.Col>
              <DataTable.Col source="discounts" label="">
                <ArrayField source="discounts" label="">
                  <DataTable
                    storeKey="product.discounts.2"
                    rowClick={false}
                    bulkActionButtons={false}
                  >
                    <DataTable.Col source="plan" label="Plan">
                      <ReferenceField
                        source="plan"
                        reference="subscription-plans"
                        label="Plan"
                        link={false}
                      >
                        <TextField source="name" />
                      </ReferenceField>
                    </DataTable.Col>
                    <DataTable.Col source="discount" label="Descuento">
                      <NumberField
                        source="discount"
                        label="Descuento"
                        options={{
                          style: "percent",
                          maximumFractionDigits: 2,
                        }}
                      />
                    </DataTable.Col>
                  </DataTable>
                </ArrayField>
              </DataTable.Col>
            </DataTable>
          </ArrayField>
        </TabbedShowLayout.Tab>

        {record?.type === PRODUCT_TYPES.PURCHASE && (
          <TabbedShowLayout.Tab label="Ofertas">
            <ArrayField
              source="offerDeliveryAvailability"
              label="Validez para Retirar"
            >
              <DataTable
                storeKey="product.offerDeliveryAvailability"
                rowClick={false}
                bulkActionButtons={false}
              >
                <DataTable.Col source="days" label="Días">
                  <FunctionField
                    source="days"
                    render={(record) =>
                      `${record.days
                        .map((d) => shortWeekdayToLong(d))
                        .join(", ")}`
                    }
                    label="Días"
                  />
                </DataTable.Col>
                <DataTable.Col source="times" label="">
                  <ArrayField source="times" label="">
                    <DataTable
                      storeKey="product.times"
                      rowClick={false}
                      bulkActionButtons={false}
                    >
                      <DataTable.Col source="opening" label="Desde">
                        <FunctionField
                          source="opening"
                          render={(timeRecord) =>
                            getTimeFromDate(
                              timeRecord.opening,
                              record.restaurantData.country
                            )
                          }
                          label="Desde"
                        />
                      </DataTable.Col>
                      <DataTable.Col source="closing" label="Hasta">
                        <FunctionField
                          source="closing"
                          render={(timeRecord) =>
                            getTimeFromDate(
                              timeRecord.closing,
                              record.restaurantData.country
                            )
                          }
                          label="Hasta"
                        />
                      </DataTable.Col>
                    </DataTable>
                  </ArrayField>
                </DataTable.Col>
              </DataTable>
            </ArrayField>
          </TabbedShowLayout.Tab>
        )}

        <TabbedShowLayout.Tab label="Stock">
          <ProductStockShowTab label={false} />
        </TabbedShowLayout.Tab>

        <TabbedShowLayout.Tab label="Imágenes">
          <ArrayField source="images" label="Imágenes">
            <DataTable
              storeKey="product.images"
              rowClick={false}
              bulkActionButtons={false}
            >
              <DataTable.Col source="url" label="">
                <ImageField source="url" label="" />
              </DataTable.Col>
            </DataTable>
          </ArrayField>
        </TabbedShowLayout.Tab>

        {record?.videos?.length > 0 && (
          <TabbedShowLayout.Tab label="Videos">
            <ArrayField source="videos" label="Videos">
              <DataTable
                storeKey="product.videos"
                rowClick={false}
                bulkActionButtons={false}
              >
                <DataTable.Col source="name" label="Nombre" />
                <DataTable.Col label="Video">
                  <VideoField label="Video" />
                </DataTable.Col>
                <DataTable.Col source="thumbnail" label="Miniatura">
                  <ImageField source="thumbnail" label="Miniatura" />
                </DataTable.Col>
                <DataTable.Col source="active" label="Activo">
                  <BooleanField source="active" label="Activo" />
                </DataTable.Col>
              </DataTable>
            </ArrayField>
          </TabbedShowLayout.Tab>
        )}

        {record?.isFromEvent === true && (
          <TabbedShowLayout.Tab label="Evento">
            <TextField source="event.name" label="Nombre" />
            <DateField
              source="event.fromPresale"
              label="Preventa desde"
              showTime
            />
            <DateField source="event.from" label="Evento desde" showTime />
            <DateField source="event.to" label="Evento hasta" showTime />
          </TabbedShowLayout.Tab>
        )}
      </TabbedShowLayout>
    </>
  );
};

const ProductShowContainer = () => (
  <Show title={<ProductTitle />} actions={<ProductShowActions />}>
    <ProductShow />
  </Show>
);

export default ProductShowContainer;
